/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Segment, Statistic } from 'semantic-ui-react'
import { UltyMoney } from '@ulty/ulty-ui'
import useUser from '../../Shared/UserProvider/useUser'
import { useTranslation } from 'react-i18next'
import { useDeviceMediaQuery } from '../../Shared/UseDeviceMediaQuery/useDeviceMediaQuery'
import { useSearchParams } from 'react-router-dom-v5-compat'
import { useUnavailableItemSummary } from './hooks'
import { getColor } from '../OpeningTimes/openingTimeHelpers'
import { RateRadialChart } from '../../Shared/RateRadialChart'
import React from 'react'

const Summary = () => {
    const [t] = useTranslation()
    const {user} = useUser()
    const {isMobile} = useDeviceMediaQuery()
    const [searchParams] = useSearchParams()

    const from = searchParams.get('from')
    const to = searchParams.get('to')

    const {data: summary} = useUnavailableItemSummary({
        from,
        to,
        enabled: !!from && !!to,
    })

    if (!summary || summary.totalMissedQuantity === 0) {
        return null
    }

    return (
      <Segment
        css={css`
            &.ui.segment {
                margin: 0;
            }
        `}
      >
          <div
            css={css`
                display: grid;
                grid-template-areas: ${isMobile ? `'a' 'd' 'c' 'b' 'e'` :
                        `"a b e"
                        "c d e"`};
                grid-template-columns: ${isMobile ? '1fr' : '1fr 1fr 1fr'};
                row-gap: .5rem;
            `}
          >
              <Statistic
                css={css`
                    grid-area: a;
                `}
                size="small"
              >
                  <Statistic.Value>{summary.totalMissedQuantity}</Statistic.Value>
                  <Statistic.Label>{t('unavailable_items.summary.total_quantity', {count: summary.totalMissedQuantity})}</Statistic.Label>
              </Statistic>

              <div
                css={css`
                    grid-area: c;
                    justify-self: center;
                    align-items: center;
                    font-size: 6pt;
                `}
              >
                  <Statistic size="mini">
                      <Statistic.Value>{summary.totalMissedSubstitutedQuantity}</Statistic.Value>
                      <Statistic.Label>{t('unavailable_items.summary.total_substituted_quantity', {count: summary.totalMissedSubstitutedQuantity})}</Statistic.Label>
                  </Statistic>
              </div>


              <div
                css={css`
                    grid-area: d;
                    justify-self: center;
                    align-items: center;
                    font-size: 6pt;
                `}>
                  <Statistic
                    color={summary.totalMissedRevenueUnavailable > 0 ? 'red' : undefined}
                    size="mini"
                  >
                      <Statistic.Value>
                          <UltyMoney
                            readonly
                            inline
                            noMargin
                            amount={summary.totalMissedRevenueUnavailable}
                            currencyCode={user.getCompanyCurrency()}
                            locale={user.getCompanyLocale()}
                          />
                      </Statistic.Value>
                      <Statistic.Label>{t('unavailable_items.summary.total_revenue_unavailable')}</Statistic.Label>
                  </Statistic>

                  <Statistic
                    color={summary.totalMissedRevenueSubstituted > 0 ? 'green' : undefined}
                    size="mini"
                  >
                      <Statistic.Value>
                          <UltyMoney
                            readonly
                            inline
                            noMargin
                            amount={summary.totalMissedRevenueSubstituted}
                            currencyCode={user.getCompanyCurrency()}
                            locale={user.getCompanyLocale()}
                          />
                      </Statistic.Value>
                      <Statistic.Label>{t('unavailable_items.summary.total_revenue_substituted')}</Statistic.Label>
                  </Statistic>
              </div>


              <Statistic
                css={css`
                    grid-area: b;
                    margin: 0 !important;
                `}
                size="small"
              >
                  <Statistic.Value>
                      <UltyMoney
                        readonly
                        inline
                        noMargin
                        amount={summary.totalMissedRevenue}
                        currencyCode={user.getCompanyCurrency()}
                        locale={user.getCompanyLocale()}
                      />
                  </Statistic.Value>
                  <Statistic.Label>{t('unavailable_items.summary.total_revenue')}</Statistic.Label>
              </Statistic>

              <div
                css={css`
                    grid-area: e;
                `}
              >
                  <RateRadialChart
                    label={t('unavailable_items.summary.substitution_rate')}
                    rate={summary.totalMissedRevenueUnavailable > 0 ? Math.round((summary.totalMissedRevenueSubstituted / summary.totalMissedRevenueUnavailable) * 100) : 0}
                    animationsEnabled
                    getColor={getColor}
                  />
              </div>
          </div>
      </Segment>
    )
}

export { Summary }
